import { Box, makeStyles } from '@material-ui/core';
import debounce from 'lodash.debounce';
import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useImmer } from 'use-immer';

import { FontSizes, TreeColors } from '../../../core/config/mainTheme';
import NoDataContainer from '../../NoDataContainer';
import { TreeInputValue, TreeItemCheckEvent, TreeItemRaw, TreeItems } from './Tree.types';
import {
  filterItems,
  getCheckedItems,
  hasSearchResult,
  initializeChecked,
  prepareModel,
  toggleChecked,
} from './Tree.utils';
import TreeGroup from './TreeGroup';
import TreeSearch from './TreeSearch';

interface Props {
  items: TreeItemRaw[];
  defaultValue?: string[];
  expanded?: string[];
  chunkLoading?: boolean;
  onCheck?: (value: TreeInputValue) => void;
}

const TreeComponent: React.FC<Props> = React.memo(
  ({ items = [], defaultValue = [], onCheck = () => {}, chunkLoading }) => {
    const classes = useStyles();
    const treeId = useMemo(() => `checkbox-${nanoid(7)}`, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const modelData = useMemo(() => prepareModel(items), []);
    const initialData = useMemo(
      () => initializeChecked(modelData, modelData, defaultValue),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    const [filteredItems, setFilteredItems] = useImmer<TreeItems>(initialData);
    const [isLoading, setIsLoading] = useState(chunkLoading);
    const [showResults, setShowResults] = useState(hasSearchResult(filteredItems));
    const searchItems = (filterText: string) => {
      // eslint-disable-next-line
			setFilteredItems((draft) => {
        draft = filterItems(draft, filterText);
      });
    };
    const debouncedFilterChange = useCallback(debounce(searchItems, 200), []);
    const onSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      debouncedFilterChange(event.target.value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onItemCheck = (event: TreeItemCheckEvent) => {
      setFilteredItems((draft: TreeItems) => {
        const data = toggleChecked(draft, event.item, event.checked);
        onCheck && onCheck(getCheckedItems(data));
        return data;
      });
    };

    useEffect(() => {
      if (chunkLoading) {
        setTimeout(() => setIsLoading(false), 100);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      setShowResults(hasSearchResult(filteredItems));
    }, [filteredItems]);

    return (
      <>
        <Box className={classes.checkboxTreeContainer}>
          <TreeSearch onSearchChange={onSearchChange} />
          <Box className={classes.checkboxTree}>
            {showResults ? (
              <ol>
                <TreeGroup
                  items={chunkLoading && isLoading ? [filteredItems[0]] : filteredItems}
                  treeId={treeId}
                  onItemCheck={onItemCheck}
                />
              </ol>
            ) : (
              <NoDataContainer />
            )}
          </Box>
        </Box>
      </>
    );
  },
);

const useStyles = makeStyles((theme) => ({
  checkboxTreeContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '400px',
    minHeight: '350px',
    maxHeight: '350px',

    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: theme.spacing(2),
    },
  },
  searchField: {
    padding: theme.spacing(2),

    '& input': {
      height: '18px',
      minHeight: '18px',
    },
    '& .MuiInputBase-formControl': {
      minHeight: '32px',
    },
  },
  checkboxTree: {
    flexGrow: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'row-reverse',
    fontSize: FontSizes.treeItem,

    '& > ol': {
      flex: '1 1 auto',
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    '& li': {
      display: 'flex',
      alignItems: 'start',

      '&.depth-2': {
        paddingLeft: '26px',
      },
      '&.depth-3': {
        paddingLeft: '52px',
      },
      '&.depth-4': {
        paddingLeft: '80px',
      },
    },
    '& ol': {
      margin: 0,
      paddingLeft: 0,
      listStyleType: 'none',
    },
    '& ol ol': {
      paddingLeft: '26px',
    },
    '& label': {
      display: 'flex',
      width: '100%',
      position: 'relative',
      marginBottom: '1px',
      borderRadius: '3px',
      cursor: 'pointer',

      '&:hover': {
        background: TreeColors.hover,
      },
      '&:active': {
        background: TreeColors.hover,
      },
      '& ol ol': {
        paddingLeft: '34px',
      },
    },
    '& button': {
      lineHeight: 'normal',
      color: 'inherit',

      '&:focus': {
        outline: 'none',
      },
      '&:disabled': {
        cursor: 'not-allowed',
      },
    },
    '& input': {
      display: 'none',
    },
  },
}));

export default TreeComponent;
